import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import type {
  EncounterAttachmentRow, EncounterDetail, EncounterResultRow, ProtocolTest, ResultFlag,
} from '@dru/types';
import {
  SAMPLE_STATUS_LABEL, SAMPLE_TYPE_LABEL, STATION_LABEL, flagWord,
} from '@dru/types';
import {
  Alert, AttentionPanel, Button, ConfirmDialog, DetailHeader, Dialog, ErrorState, IconAction, InfoTip, Input, RowActions, Select,
  SelectField, StatusBadge, TextSkeleton, cn, type CheckItem, type Tone,
} from '@dru/ui';
import { AlertTriangle, Check, ChevronLeft, Eye, Paperclip, Trash2 } from 'lucide-react';
import { VITAL_CODE, ageInYears, derivedFromCodes } from '@dru/clinical-math/derived-vitals';
import { DerivedVitalsPanel } from '../../../components/DerivedVitalsPanel';
import { ToothChart } from '../../../components/ToothChart';
import { TOOTH_CHART_CODE, formatToothChart } from '@dru/clinical-math/dental';
import { suggestionListFor } from '@dru/clinical-math/suggestions';
import { useSuggestions } from '../../../lib/useSuggestions';
import { api } from '../../../lib/api';
import { parseMeasured } from '../../../lib/measured';
import { useQuery } from '../../../lib/useQuery';
import { errorMessage } from '../../../lib/auth';
import { useAuth } from '../../../lib/auth';
import { holds } from '../../../layouts/ConsoleShell';
import { usePartner, useVenuePrefix } from '../usePartner';
import { ScanLookup } from '../components/ScanLookup';
import { examTypeLabel } from './Queue';
import { ProfileReports } from '../../../components/ProfileReports';
import { ClinicalComposerButton } from '../../../components/ClinicalComposer';
import { IdentityPanel } from '../../physician/components/ReviewAssurance';
import type { TemplateContext } from '@dru/clinical-math/smart-template';

/**
 * One worker's visit, station by station.
 *
 * The station is the unit of work on the floor — a person sits at audiometry
 * and records two thresholds — so the screen is organised by station rather
 * than by test. Each one starts, takes its measurements, and completes.
 *
 * A value is recorded against the test it belongs to, and the API refuses a
 * test that is not measured at that station or not in this order's protocol
 * snapshot. Nothing here can widen the battery: what the examination owes was
 * decided when it was raised.
 */
export function Encounter() {
  const { id = '' } = useParams();
  const prefix = useVenuePrefix();
  const { permissions } = useAuth();
  const { partnerId } = usePartner();

  const encounter = useQuery<EncounterDetail>(
    () => api.get(`/encounters/${id}`), [id]);

  const [banner, setBanner] = useState<string | null>(null);
  const [drawing, setDrawing] = useState(false);
  const [closing, setClosing] = useState<'COMPLETE' | 'PARTIAL' | null>(null);

  const back = (
    <Link to={`${prefix}/queue`} className="inline-flex min-h-6 items-center tap-area gap-1 text-small text-neutral-600 hover:text-neutral-900 hover:underline">
      <ChevronLeft className="h-4 w-4" aria-hidden />
      Back to the queue
    </Link>
  );

  if (encounter.loading && !encounter.data) {
    return <div className="space-y-4">{back}<TextSkeleton lines={8} /></div>;
  }
  if (encounter.error || !encounter.data) {
    return (
      <div className="space-y-4">
        {back}
        <ErrorState title="Could not open this encounter" detail={encounter.error ?? undefined} onRetry={encounter.reload} />
      </div>
    );
  }

  const e = encounter.data;
  const tests = e.order.protocolSnapshot?.tests ?? [];
  const canEnter = holds(permissions, 'encounter.station-enter');
  const canComplete = holds(permissions, 'encounter.complete');
  const canPartial = holds(permissions, 'encounter.partial');
  const canDraw = holds(permissions, 'lab.sample-accession');
  // The desk runs the day; it does not read the measurements. Values and
  // flags are for the people who take and amend them — everyone else sees
  // whether a test is recorded, which is all closing the encounter needs.
  const seesValues = canEnter || holds(permissions, 'encounter.station-amend');
  const closed = !!e.completedAt;
  const partial = e.completionType === 'PARTIAL';

  // Latest attempt per test: re-entry is a new attempt, never an overwrite,
  // and what the floor needs to see is the value that stands now.
  const latest = new Map<string, EncounterResultRow>();
  for (const r of e.results) {
    const prev = latest.get(r.testId);
    if (!prev || r.attemptNo > prev.attemptNo) latest.set(r.testId, r);
  }

  // A pathology value belongs to a sample with a chain of custody, and the
  // API refuses one entered at a station. So a station lists only what is
  // measured there; the tubes it draws are under Samples.
  const labTests = tests.filter((t) => t.category === 'PATHOLOGY');
  const stationTests = (station: string) => {
    const own = tests.filter((t) => t.station === station && t.category !== 'PATHOLOGY');
    // The snapshot merges protocols and loses their order; the vitals tray
    // is read top to bottom in the order it is taken.
    return station === 'VITALS'
      ? [...own].sort((a, b) => vitalsRank(a.code) - vitalsRank(b.code))
      : own;
  };
  const missing = tests.filter((t) => t.isMandatory && t.category !== 'PATHOLOGY' && !latest.has(t.id));
  const hasLab = labTests.length > 0;
  const liveSamples = e.samples.filter((x) => !['REJECTED', 'RECOLLECT_REQUESTED', 'ORDERED'].includes(x.status)).length;
  // The laboratory cannot start on a case with no tube; the API refuses to
  // complete one, so the button says why instead of failing.
  const tubesMissing = hasLab && liveSamples === 0;

  const done = e.stations.filter((st) => st.status === 'COMPLETED').length;
  // The floor's order: every measuring station, then the consultation, which
  // reads what the others recorded.
  const ordered = [...e.stations].sort((a, b) => Number(a.station === 'PHYSICIAN') - Number(b.station === 'PHYSICIAN'));
  const next = closed ? null : ordered.find((st) => st.status !== 'COMPLETED' && st.status !== 'SKIPPED') ?? null;
  const items: CheckItem[] = e.stations.map((st) => {
    const own = stationTests(st.station);
    const recorded = own.filter((t) => latest.has(t.id)).length;
    const draws = own.length === 0 && labTests.some((t) => t.station === st.station);
    // A station left short only matters when the encounter closed short.
    const state: CheckItem['state'] = st.status === 'COMPLETED' ? 'done'
      : closed && partial ? 'warning' : 'todo';
    const word = stationWord(st.status, closed);
    return {
      key: st.id,
      label: STATION_LABEL[st.station] ?? st.station,
      state,
      detail: draws
        ? `${word} · samples drawn here: ${e.samples.length}`
        : own.length ? `${word} · ${recorded} of ${own.length} recorded` : word,
    };
  });

  const yourPart = closed
    ? (partial
      ? 'Closed as partial. The case has not gone on to the laboratory or a physician.'
      : hasLab ? 'Closed. Results now move through the laboratory, then to the physician.'
        : 'Closed. The case has gone to the physician for review.')
    : canEnter
      ? 'Type or choose each reading at your station below, then record them together. The station completes when nothing required is left.'
      : canComplete
        ? (missing.length
          ? `Close the encounter when the floor is done. ${missing.length} required reading${missing.length === 1 ? '' : 's'} still to take.`
          : tubesMissing
            ? 'Every reading is in, but no sample has been drawn for the laboratory. Draw the tubes, or close as partial.'
            : 'Every required reading is in. Close the encounter to send it on.')
        : 'You can follow this encounter. Readings are taken by the station technicians; the front desk closes it.';

  return (
    <div className="space-y-5">
      <DetailHeader
        back={back}
        eyebrow="Encounter"
        title={e.order.employee.fullName}
        badges={(
          <>
            {closed
              ? <StatusBadge tone={partial ? 'dueSoon' : 'covered'}>{partial ? 'Closed — partial' : 'Closed — complete'}</StatusBadge>
              : <StatusBadge tone="pending">In progress</StatusBadge>}
            <IdentityChip method={e.identityMethod} />
          </>
        )}
        subtitle={(
          <span className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
            <span className="font-mono">{e.order.employee.employeeCode}</span>
            <span aria-hidden>·</span>
            <span>{e.order.employee.sex ? sexWord(e.order.employee.sex) : 'sex not recorded'}</span>
            <span aria-hidden>·</span>
            <span>{age(e.order.employee.dateOfBirth)}</span>
            <span aria-hidden>·</span>
            <span>{examTypeLabel(e.order.examinationType)}</span>
            <span aria-hidden>·</span>
            <span>checked in {when(e.checkedInAt)}</span>
            {e.completedAt && (<><span aria-hidden>·</span><span>closed {when(e.completedAt)}</span></>)}
          </span>
        )}
      />

      {e.identityMethod === 'PHONE_SEARCH' && (
        <Alert tone="warning" title="Identity not verified">
          This employee was found by phone search, which located the booking but did not prove who attended.
          The physician sees this when they certify.
        </Alert>
      )}

      {/* Requirement 3: the record photo beside the person, for identity only. */}
      <IdentityPanel base={`/encounters/${id}`} canRecord={!closed && canEnter} />

      {banner && (
        <div role="status" className="rounded-card border border-covered-600/25 bg-covered-100 px-3 py-2 text-small text-covered-600">
          {banner}
        </div>
      )}

      <AttentionPanel
        title={`Stations — ${done} of ${e.stations.length} done`}
        items={items}
        summary={yourPart}
        actions={!closed && (canComplete || canPartial) ? (
          <>
            {canPartial && (
              <Button variant="secondary" onClick={() => setClosing('PARTIAL')}>Close as partial…</Button>
            )}
            {canComplete && (
              <Button
                onClick={() => setClosing('COMPLETE')}
                disabled={missing.length > 0 || tubesMissing}
                title={missing.length ? 'Every required reading has to be recorded first'
                  : tubesMissing ? 'Draw the samples for the laboratory first, or close as partial' : undefined}
              >
                Complete encounter…
              </Button>
            )}
          </>
        ) : undefined}
      />

      {/* One document per report profile; on a closed encounter it is the
          next thing to do (verify ECG, X-ray, PFT, audiometry, measurements,
          vitals), so it comes before the station detail. */}
      {closed && holds(permissions, 'encounter.read') && (
        <ProfileReports audience={{ kind: 'venue', encounterId: e.id, canDownload: holds(permissions, 'lab.report-download') }} />
      )}

      {!closed && canComplete && missing.length > 0 && (
        <p className="-mt-2 text-small text-neutral-600">
          <span className="font-medium text-neutral-800">Still to record: </span>
          {missing.map((t) => (t.station ? `${t.name} (${STATION_LABEL[t.station] ?? t.station})` : t.name)).join(', ')}.
          {' '}If the employee cannot finish today, close as partial with a reason.
        </p>
      )}

      {!closed && canEnter && (() => {
        const ready = e.stations.filter((st) => st.status === 'IN_PROGRESS'
          && stationTests(st.station).filter((t) => t.isMandatory).every((t) => latest.has(t.id))
          // Not "ready" just because it has nothing to record: a station that
          // only draws is ready when its tubes exist.
          && !(stationTests(st.station).length === 0
            && labTests.some((t) => t.station === st.station) && liveSamples === 0));
        if (ready.length < 2) return null;
        return (
          <CompleteAll encounterId={e.id} stations={ready.map((st) => st.station)}
            onDone={() => { setBanner(`${ready.length} stations completed.`); encounter.reload(); }} />
        );
      })()}

      {/* Where this person is in the floor, and a jump to the next station.
          Sticky under the top bar so a technician scrolling a long card can
          still see what is left. */}
      {e.stations.length > 1 && (
        <StationRail stations={ordered} next={next?.id ?? null} closed={closed} />
      )}

      {/* Station staff scan the next person here and land on their visit —
          the same encounter the desk opened, never a second one. */}
      {!closed && canEnter && partnerId && (
        <ScanLookup partnerId={partnerId} prefix={prefix} canCheckIn={holds(permissions, 'appointment.checkin')} compact />
      )}

      <div className="space-y-4">
        {ordered.map((station) => (
          <div key={station.id} id={`station-${station.id}`} className="scroll-mt-32">
          <StationCard
            key={station.id}
            encounterId={e.id}
            station={station}
            tests={stationTests(station.station)}
            who={e.order.employee}
            attachments={(e.attachments ?? []).filter((a) => a.station === station.station)}
            drawsSamples={labTests.some((t) => t.station === station.station)}
            samplesDrawn={liveSamples}
            latest={latest}
            closed={closed}
            readOnly={!canEnter || closed}
            seesValues={seesValues}
            onChanged={(msg) => { setBanner(msg); encounter.reload(); }}
          />
          </div>
        ))}
      </div>

      {/* While the floor is working, the stations come first. */}
      {!closed && holds(permissions, 'encounter.read') && (
        <ProfileReports audience={{ kind: 'venue', encounterId: e.id, canDownload: holds(permissions, 'lab.report-download') }} />
      )}

      {hasLab && (
        <section className="rounded-card border border-neutral-200 bg-white p-4" aria-labelledby="samples-h">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="min-w-0 flex-[1_1_20rem]">
              <div className="flex items-center gap-1">
                <h2 id="samples-h" className="text-h4 text-neutral-900">Samples for the laboratory</h2>
                <InfoTip label="About samples for the laboratory">
                  A pathology value is never entered at a station, because it belongs to a sample with a chain of custody.
                </InfoTip>
              </div>
              <p className="mt-1 text-small text-neutral-600">
                {labTests.length} pathology test{labTests.length === 1 ? ' is' : 's are'} drawn here and resulted by the laboratory.
              </p>
            </div>
            {/* The one act that used to have no button. A tube that is never
                drawn here never reaches the bench. */}
            {canDraw && !closed && (
              <div className="flex flex-wrap items-center gap-2">
                <Button size="sm" variant="secondary" onClick={() => setDrawing(true)}>Draw one tube…</Button>
                {/* The tubes the order needs, derived from its tests: a CBC and
                    a creatinine are an EDTA and a serum, and the desk should
                    not have to know that. One barcode per tube, as always. */}
                <DeriveSamples encounterId={e.id}
                  onDone={(n, closedPhlebotomy) => {
                    setBanner((n === 0 ? 'Every tube this order needs is already drawn.' : `${n} sample${n === 1 ? '' : 's'} drawn.`)
                      + (closedPhlebotomy ? ' Phlebotomy completed.' : ''));
                    encounter.reload();
                  }} />
              </div>
            )}
          </div>
          {e.samples.length === 0 ? (
            <p className="mt-3 rounded-input border border-warning-600/25 bg-warning-100 px-3 py-2 text-small text-warning-600">
              No sample drawn yet. The laboratory cannot start until one exists.
            </p>
          ) : (
            <ul className="mt-3 divide-y divide-neutral-100 rounded-input border border-neutral-200">
              {e.samples.map((s) => (
                <SampleRow key={s.id} sample={s} canDraw={canDraw}
                  onCollected={() => { setBanner(`Replacement sample ${s.barcode} collected.`); encounter.reload(); }} />
              ))}
            </ul>
          )}
        </section>
      )}

      {drawing && (
        <DrawSampleDialog
          encounterId={e.id}
          tests={labTests}
          onClose={() => setDrawing(false)}
          onDone={(barcode) => { setDrawing(false); setBanner(`Sample ${barcode} drawn.`); encounter.reload(); }}
        />
      )}

      {closing === 'COMPLETE' && (
        <ConfirmDialog
          title={`Complete ${e.order.employee.fullName}’s encounter?`}
          what={hasLab
            ? 'The encounter closes and the case moves on to the laboratory. Once every pathology result is released it goes to the physician.'
            : 'The encounter closes and the case goes to the physician for review.'}
          affects={`${done} of ${e.stations.length} stations are marked done. Nothing more can be recorded at the stations once it is closed.`}
          irreversible="A closed encounter cannot be reopened from this screen."
          confirmLabel="Complete encounter"
          onCancel={() => setClosing(null)}
          onConfirm={async () => {
            await closeEncounter(e.id, 'COMPLETE');
            setClosing(null); setBanner('Encounter completed.'); encounter.reload();
          }}
        />
      )}
      {closing === 'PARTIAL' && (
        <PartialDialog
          name={e.order.employee.fullName}
          missing={missing.map((t) => t.name)}
          onClose={() => setClosing(null)}
          onDone={() => { setClosing(null); setBanner('Encounter closed as partial.'); encounter.reload(); }}
          encounterId={e.id}
        />
      )}
    </div>
  );
}

/** The stations as a row of jump links: done, under way, still to do — and which is next. */
function StationRail({ stations, next, closed }: {
  stations: EncounterDetail['stations'];
  next: string | null;
  closed: boolean;
}) {
  const jump = (id: string) => document.getElementById(`station-${id}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  const nextStation = stations.find((st) => st.id === next);
  return (
    <nav aria-label="Stations" className="sticky top-14 z-20 rounded-card border border-neutral-200 bg-white/95 px-3 py-2 shadow-card backdrop-blur">
      <div className="relative flex items-center gap-2 overflow-x-auto">
        {nextStation && (
          <Button size="sm" onClick={() => jump(nextStation.id)} className="shrink-0">
            Next: {STATION_LABEL[nextStation.station] ?? nextStation.station}
          </Button>
        )}
        {!nextStation && !closed && (
          <span className="shrink-0 text-small font-semibold text-covered-600">All stations done</span>
        )}
        <ol className="flex items-center gap-1.5">
          {stations.map((st) => {
            const isDone = st.status === 'COMPLETED';
            const isNow = st.status === 'IN_PROGRESS';
            return (
              <li key={st.id} className="shrink-0">
                <button
                  type="button"
                  onClick={() => jump(st.id)}
                  aria-current={st.id === next ? 'step' : undefined}
                  className={cn(
                    'inline-flex h-8 items-center gap-1 rounded-full border px-2.5 text-caption font-medium transition-colors max-md:h-10',
                    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500',
                    isDone ? 'border-covered-600/30 bg-covered-100 text-covered-600'
                      : isNow ? 'border-pending-600/30 bg-pending-100 text-pending-600'
                        : st.id === next ? 'border-primary-500 bg-primary-50 text-primary-700'
                          : 'border-neutral-200 bg-white text-neutral-600 hover:bg-neutral-50',
                  )}
                >
                  {isDone ? <Check className="h-3 w-3" aria-hidden /> : <span aria-hidden className={cn('h-1.5 w-1.5 rounded-full', isNow ? 'bg-pending-600' : 'bg-neutral-400')} />}
                  {STATION_LABEL[st.station] ?? st.station}
                  <span className="sr-only"> — {isDone ? 'done' : isNow ? 'in progress' : st.status === 'SKIPPED' ? 'skipped' : 'to do'}</span>
                </button>
              </li>
            );
          })}
        </ol>
      </div>
    </nav>
  );
}

async function closeEncounter(encounterId: string, type: 'COMPLETE' | 'PARTIAL', reason?: string) {
  try {
    await api.post(`/encounters/${encounterId}/complete`, {
      completionType: type,
      ...(type === 'PARTIAL' ? { partialReasonCode: reason } : {}),
    }, crypto.randomUUID());
  } catch (err) {
    throw new Error(errorMessage(err));
  }
}

const PARTIAL_REASONS: Array<[string, string]> = [
  ['SAMPLE_PENDING', 'Sample pending'],
  ['TEST_PENDING', 'Test pending'],
  ['DOCUMENT_PENDING', 'Document pending'],
  ['EMPLOYEE_ABSENT', 'Employee left'],
  ['MACHINE_UNAVAILABLE', 'Machine unavailable'],
  ['RETEST_REQUIRED', 'Retest required'],
];

/** Closing short of complete: says what it means, and asks why. */
function PartialDialog({ name, missing, encounterId, onClose, onDone }: {
  name: string; missing: string[]; encounterId: string; onClose: () => void; onDone: () => void;
}) {
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  async function go() {
    setBusy(true); setError(null);
    try { await closeEncounter(encounterId, 'PARTIAL', reason); onDone(); }
    catch (err) { setError(err instanceof Error ? err.message : String(err)); setBusy(false); }
  }
  return (
    <Dialog title={`Close ${name}’s encounter as partial?`} onClose={busy ? () => undefined : onClose}>
      <div className="mt-3 space-y-3">
        <p className="text-body text-neutral-700">
          The encounter closes, but the case does <strong>not</strong> go on to the laboratory or a physician.
          It stays in progress, so a half-finished examination never reaches a doctor looking complete.
        </p>
        {missing.length > 0 && (
          <div className="rounded-input border border-neutral-200 bg-neutral-50 px-3 py-2 text-small text-neutral-700">
            Not recorded: {missing.join(', ')}.
          </div>
        )}
        <Alert tone="warning">Nothing more can be recorded here once it is closed. The reason is kept on the record.</Alert>
        <SelectField label="Why is it partial?" value={reason} onChange={setReason} required>
          <option value="">Choose a reason…</option>
          {PARTIAL_REASONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </SelectField>
        {error && <Alert tone="danger">{error}</Alert>}
      </div>
      <div className="mt-5 flex justify-end gap-2">
        <Button variant="secondary" onClick={onClose} disabled={busy}>Cancel</Button>
        <Button onClick={go} loading={busy} disabled={!reason}>Close as partial</Button>
      </div>
    </Dialog>
  );
}

function SampleRow({ sample: s, canDraw, onCollected }: {
  sample: EncounterDetail['samples'][number]; canDraw: boolean; onCollected: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  async function collect() {
    setBusy(true); setError(null);
    try {
      await api.post(`/lab/samples/${s.id}/transition`, { to: 'COLLECTED' });
      onCollected();
    } catch (err) { setError(errorMessage(err)); } finally { setBusy(false); }
  }
  return (
    <li className="flex flex-wrap items-center gap-x-3 gap-y-1 px-3 py-2 text-small">
      <span className="font-mono text-neutral-800">{s.barcode}</span>
      <span className="text-neutral-600">{SAMPLE_TYPE_LABEL[s.sampleType] ?? s.sampleType}</span>
      <StatusBadge tone={s.status === 'REJECTED' || s.status === 'RECOLLECT_REQUESTED' ? 'overdue' : s.status === 'ORDERED' ? 'pending' : 'neutral'}>
        {SAMPLE_STATUS_LABEL[s.status] ?? s.status}
      </StatusBadge>
      {s.status === 'ORDERED' && canDraw && (
        <Button size="sm" variant="secondary" className="ml-auto" loading={busy} onClick={collect}>
          Collect replacement
        </Button>
      )}
      {error && <p role="alert" className="w-full text-caption text-danger-600">{error}</p>}
    </li>
  );
}

function stationWord(status: string, closed: boolean): string {
  switch (status) {
    case 'COMPLETED': return 'Done';
    case 'IN_PROGRESS': return closed ? 'Left open' : 'In progress';
    case 'SKIPPED': return 'Skipped';
    default: return closed ? 'Not done' : 'Not started';
  }
}

const TIME_FMT = new Intl.DateTimeFormat('en-IN', { hour: 'numeric', minute: '2-digit' });
const DAY_TIME_FMT = new Intl.DateTimeFormat('en-IN', { day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit' });
/** "9:28 pm" today, "23 Sept, 9:28 pm" otherwise. */
function when(v: string): string {
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toDateString() === new Date().toDateString() ? TIME_FMT.format(d) : DAY_TIME_FMT.format(d);
}

function sexWord(sex: string): string {
  const s = sex.toUpperCase();
  return s === 'MALE' || s === 'M' ? 'male' : s === 'FEMALE' || s === 'F' ? 'female' : sex.toLowerCase();
}

function IdentityChip({ method }: { method: EncounterDetail['identityMethod'] }) {
  if (method === 'PHONE_SEARCH') {
    return <StatusBadge tone="restricted">Identity not verified</StatusBadge>;
  }
  return (
    <StatusBadge tone="covered">
      <Check className="h-3 w-3" aria-hidden />
      Identity verified — {method === 'QR' ? 'QR pass' : 'employee code'}
    </StatusBadge>
  );
}

type ValueKind = 'NUMERIC' | 'TEXT' | 'BOOLEAN' | 'ENUM';

/**
 * The physician's station is a questionnaire followed by an examination;
 * forty rows in snapshot order are unreadable. Grouped by what the code
 * says the question is about, in the order a consultation runs. Every other
 * station is one list.
 */
const PHYSICIAN_GROUPS: Array<[string, (code: string) => boolean]> = [
  ['Present complaints', (c) => ['HIST-COMPLAINTS', 'HIST-NOTIFIABLE', 'HIST-TREATMENT'].includes(c)],
  ['Medical history', (c) => c.startsWith('HIST-') && !/^HIST-(FAMILY|SMOKING|TOBACCO|GUTKHA|ALCOHOL)$/.test(c)],
  ['Family history', (c) => c.startsWith('FAM-') || c === 'HIST-FAMILY'],
  ['Occupation and lifestyle', (c) => c.startsWith('OCC-') || c.startsWith('LIFE-') || /^HIST-(SMOKING|TOBACCO|GUTKHA|ALCOHOL)$/.test(c)],
  ['Immunisation and wellbeing', (c) => c.startsWith('IMM-') || c.startsWith('WELL-')],
  ['Examination', (c) => c.startsWith('PHY-') && !/^PHY-(IMPRESSION|DIAGNOSIS|TREATMENT|REMARKS)$/.test(c)],
  ["Physician's notes", (c) => /^PHY-(IMPRESSION|DIAGNOSIS|TREATMENT|REMARKS)$/.test(c)],
];
const DENTAL_GROUPS: Array<[string, (code: string) => boolean]> = [
  ['Oral health and habits', (c) => ['DEN-COMPLAINTS', 'DEN-HYGIENE', 'DEN-BRUSHING', 'DEN-TOBACCO'].includes(c)],
  ['Teeth', (c) => ['DEN-CHART', 'DEN-SENSITIVITY', 'DEN-MALALIGNMENT'].includes(c)],
  ['Gums and oral mucosa', (c) => /^DEN-(GINGIVITIS|PERIODONTITIS|GUM-|PUS|ULCERS|PATCHES|LEUKOPLAKIA)/.test(c)],
  ['Tongue and jaw', (c) => /^DEN-(TONGUE|TMJ|JAW)/.test(c)],
  ['Lesions and diagnosis', (c) => /^DEN-(LESION|BIOPSY|DIAGNOSIS|TREATMENT|REFERRAL|EDUCATION)/.test(c)],
  ['Remarks', (c) => c === 'DEN-REMARKS'],
];
const VISION_GROUPS: Array<[string, (code: string) => boolean]> = [
  ['Statutory', (c) => c === 'VIS-ACUITY' || c === 'VIS-COLOUR'],
  ['General', (c) => c === 'VIS-GLASSES' || c === 'VIS-SURGERY'],
  ['Visual acuity', (c) => /^VIS-(DIST|NEAR)-/.test(c)],
  ['Spectacle prescription', (c) => c.startsWith('VIS-RX-') || c === 'VIS-REFRACTIVE-ERROR'],
  ['Eye structure', (c) => /^VIS-(EYELIDS|CONJUNCTIVA|CORNEA|SCLERA|IRIS|PUPIL|MOVEMENTS|SQUINT|NYSTAGMUS)/.test(c)],
  ['Fundus', (c) => /^VIS-(OPTIC-DISC|RETINA|MACULA)/.test(c)],
  ['Diagnosis and complaints', (c) => /^VIS-(DIAGNOSIS|REFERRAL|COMPLAINTS|OBSERVATIONS|CORRECTION)/.test(c)],
];
const STATION_GROUPS: Record<string, Array<[string, (code: string) => boolean]>> = {
  PHYSICIAN: PHYSICIAN_GROUPS, DENTAL: DENTAL_GROUPS, VISION: VISION_GROUPS,
};
function groupTests(station: string, tests: ProtocolTest[]): Array<[string | null, ProtocolTest[]]> {
  const groups = STATION_GROUPS[station];
  // A station with one or two tests reads better as a plain list.
  if (!groups || tests.length <= 2) return [[null, tests]];
  const out: Array<[string | null, ProtocolTest[]]> = [];
  const placed = new Set<string>();
  for (const [heading, matches] of groups) {
    const list = tests.filter((t) => !placed.has(t.id) && matches(t.code));
    if (!list.length) continue;
    for (const t of list) placed.add(t.id);
    out.push([heading, list]);
  }
  const rest = tests.filter((t) => !placed.has(t.id));
  if (rest.length) out.push([out.length ? 'Other' : null, rest]);
  return out;
}

const VITALS_ORDER = [
  VITAL_CODE.HEIGHT, VITAL_CODE.WEIGHT, VITAL_CODE.BMI, VITAL_CODE.WAIST,
  VITAL_CODE.SYSTOLIC, VITAL_CODE.DIASTOLIC, VITAL_CODE.PULSE, VITAL_CODE.TEMPERATURE, VITAL_CODE.SPO2,
] as const;
/** Where a vitals reading sits on the tray; anything unlisted keeps its place after them. */
function vitalsRank(code: string): number {
  const i = (VITALS_ORDER as readonly string[]).indexOf(code);
  return i === -1 ? VITALS_ORDER.length : i;
}

/**
 * Which composer contexts a free-text test offers (Requirement 4). Only the
 * history and the physician's notes: a measured value is never composed.
 */
function composerContexts(code: string): TemplateContext[] | null {
  if (/^PHY-TREATMENT$/.test(code)) return ['PRESCRIPTION', 'CONSULTATION', 'REFERRAL'];
  if (/^PHY-(IMPRESSION|DIAGNOSIS|REMARKS)$/.test(code)) return ['CONSULTATION', 'REFERRAL', 'INVESTIGATION'];
  if (/^(HIST|FAM|OCC|LIFE|IMM)-/.test(code)) return ['MEDICAL_HISTORY'];
  if (/^(VIS|DEN|AUD|PFT|ECG|XRAY)-.*(REMARKS|OBSERVATIONS|DIAGNOSIS|FINDINGS?)$/.test(code)) return ['INVESTIGATION'];
  return null;
}

function kindOf(test: ProtocolTest): ValueKind {
  if (test.valueType) return test.valueType;
  // The snapshot does not always carry valueType; a unit means a number.
  return test.unit ? 'NUMERIC' : 'TEXT';
}

/**
 * The answer that means "nothing found", where the test has exactly one.
 *
 * A normal/abnormal test's "normal"; a fixed answer set's one answer that is
 * not a finding (colour vision's "normal trichromatic"). Where several answers
 * are normal — Snellen's 6/5, 6/6 and 6/9, the chest film's three "no
 * opacities" grades — there is no single normal to assume, and "mark all
 * normal" leaves the test for the technician to choose. Never a number: a
 * blood pressure is measured, not presumed.
 */
function normalAnswer(test: ProtocolTest): string | null {
  const kind = kindOf(test);
  if (kind === 'BOOLEAN') return 'true';
  if (kind !== 'ENUM') return null;
  const opts = test.answerOptions ?? [];
  const normal = opts.filter((o) => o.isAbnormal === false);
  if (normal.length === 1) return normal[0]!.code;
  const named = normal.find((o) => o.code === 'NORMAL');
  if (named) return named.code;
  // A yes/no question whose finding is "yes" — the questionnaire's history,
  // exposure and habit questions. "No" is its nothing-found, even beside
  // "unknown" and "not assessed", which are answers about the asking.
  const no = normal.find((o) => o.code === 'NO');
  return no && opts.some((o) => o.code === 'YES' && o.isAbnormal) ? no.code : null;
}

/** The body a value is sent as, or the reason it cannot be. */
function resultBody(test: ProtocolTest, raw: string): { ok: true; body: Record<string, unknown> } | { ok: false; message: string } {
  const kind = kindOf(test);
  const body: Record<string, unknown> = { testId: test.id };
  if (kind === 'NUMERIC') {
    // Read before anything is sent, and before the station is started on the
    // strength of it: "abc" used to go out as null and come back stored as 0.
    const m = parseMeasured(raw, { unit: test.unit, plausible: test.plausibleRange });
    if (!m.ok) return { ok: false, message: m.message };
    body.valueNumeric = m.value;
  } else if (kind === 'BOOLEAN') body.valueBoolean = raw === 'true';
  else if (kind === 'ENUM') body.valueOptionCode = raw;
  else body.valueText = raw;
  return { ok: true, body };
}

/**
 * One station: its readings as one form.
 *
 * Every value typed or chosen is held here, so the station records them
 * together — "Record all" — and closes itself when that leaves nothing
 * required. A single row can still be recorded on its own. The first value
 * starts the station (that is what puts the operator's name on it), and the
 * last one closes it when nothing else, optional or required, is left to
 * take. Around four clicks a station became one or two (flow audit 8.1).
 */
function StationCard({ encounterId, station, tests, who, attachments, drawsSamples, samplesDrawn, latest, closed, readOnly, seesValues, onChanged }: {
  encounterId: string;
  station: EncounterDetail['stations'][number];
  tests: ProtocolTest[];
  /** Whose readings: the derived figures band on sex and age. */
  who: { sex: string | null; dateOfBirth: string | null };
  /** The machine's own paper attached at this station. */
  attachments: EncounterAttachmentRow[];
  drawsSamples: boolean;
  /** Tubes drawn on this encounter that still stand (not rejected). */
  samplesDrawn: number;
  latest: Map<string, EncounterResultRow>;
  closed: boolean;
  readOnly: boolean;
  seesValues: boolean;
  onChanged: (message: string) => void;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notes, setNotes] = useState('');
  const [values, setValues] = useState<Record<string, string>>({});
  const [rowErrors, setRowErrors] = useState<Record<string, string>>({});
  const [savingRow, setSavingRow] = useState<string | null>(null);

  const name = STATION_LABEL[station.station] ?? station.station;
  const done = tests.filter((t) => latest.has(t.id)).length;
  const requiredLeft = tests.filter((t) => t.isMandatory && !latest.has(t.id)).length;
  // A station that only draws has no reading to wait for, so "every test is
  // in" was true of it the moment it opened and it could be closed with no
  // tube drawn. Its work is the tubes; the API refuses the same thing.
  const tubesOwed = drawsSamples && tests.length === 0 && samplesDrawn === 0;
  const editable = !readOnly && station.status !== 'COMPLETED';

  // What the readings work out to, from what is typed over what is recorded.
  // Only the vitals station has anything to work out; BMI is one of its
  // tests but is recorded by the API from height and weight, so its row
  // shows the figure and takes no typing.
  const numericByCode = new Map<string, number>();
  for (const t of tests) {
    const typed = (values[t.id] ?? '').trim();
    const v = typed !== '' && /^[+-]?(\d+(\.\d*)?|\.\d+)$/.test(typed) ? Number(typed) : (latest.get(t.id)?.valueNumeric ?? null);
    if (v != null && Number.isFinite(v)) numericByCode.set(t.code, v);
  }
  const derived = station.station === 'VITALS'
    ? derivedFromCodes(numericByCode, { sex: who.sex, ageYears: ageInYears(who.dateOfBirth) })
    : null;
  const bmiTest = tests.find((t) => t.code === VITAL_CODE.BMI) ?? null;
  const bmiDerived = !!bmiTest
    && tests.some((t) => t.code === VITAL_CODE.HEIGHT) && tests.some((t) => t.code === VITAL_CODE.WEIGHT);
  const derivable = (t: ProtocolTest) => bmiDerived && t.id === bmiTest!.id;
  /** Will the API hold a value for this test once what is typed is recorded? */
  const willHave = (t: ProtocolTest) => latest.has(t.id) || (values[t.id] ?? '') !== ''
    || (derivable(t) && numericByCode.has(VITAL_CODE.HEIGHT) && numericByCode.has(VITAL_CODE.WEIGHT));

  const filled = tests.filter((t) => !derivable(t) && (values[t.id] ?? '') !== '');
  // What "Record all" would leave owed; when nothing required, it closes too.
  const requiredAfter = tests.filter((t) => t.isMandatory && !willHave(t)).length;
  const closesOnRecord = filled.length > 0 && requiredAfter === 0 && !tubesOwed;
  const normalable = tests.filter((t) => !latest.has(t.id) && (values[t.id] ?? '') === '' && normalAnswer(t) !== null);

  const setValue = (testId: string, v: string) => {
    setValues((m) => ({ ...m, [testId]: v }));
    if (rowErrors[testId]) setRowErrors((m) => { const n = { ...m }; delete n[testId]; return n; });
  };

  const path = (what: string) => `/encounters/${encounterId}/stations/${station.station}/${what}`;
  const start = async () => {
    if (station.status === 'PENDING') await api.post(path('start'), {}, crypto.randomUUID());
  };
  const complete = () => api.post(path('complete'), { notes: notes.trim() || undefined }, crypto.randomUUID());

  async function act(fn: () => Promise<unknown>, message: string) {
    setBusy(true); setError(null);
    try { await fn(); onChanged(message); } catch (e) { setError(errorMessage(e)); } finally { setBusy(false); }
  }

  /**
   * Records every filled value, then closes the station when that leaves
   * nothing required. Every value is read before anything is sent: one typo
   * among six readings must not leave three of them saved and three not.
   */
  async function recordAll() {
    const bodies: Array<{ test: ProtocolTest; body: Record<string, unknown> }> = [];
    const bad: Record<string, string> = {};
    for (const t of filled) {
      const r = resultBody(t, values[t.id]!);
      if (r.ok) bodies.push({ test: t, body: r.body }); else bad[t.id] = r.message;
    }
    if (Object.keys(bad).length) { setRowErrors(bad); return; }

    setBusy(true); setError(null);
    let saved = 0;
    try {
      await start();
      for (const { test, body } of bodies) {
        try {
          await api.post(path('results'), body, crypto.randomUUID());
          saved++;
          setValues((m) => { const n = { ...m }; delete n[test.id]; return n; });
        } catch (e) {
          setRowErrors((m) => ({ ...m, [test.id]: errorMessage(e) }));
        }
      }
      if (saved === bodies.length && closesOnRecord) {
        await complete();
        onChanged(`${name}: ${saved} recorded, station completed.`);
      } else {
        onChanged(saved === bodies.length
          ? `${name}: ${saved} recorded.`
          : `${name}: ${saved} of ${bodies.length} recorded — see the rows marked below.`);
      }
    } catch (e) {
      setError(errorMessage(e));
      if (saved) onChanged(`${name}: ${saved} recorded.`);
    } finally { setBusy(false); }
  }

  /** One row on its own. Closes the station when it was the last test of any kind. */
  async function recordOne(test: ProtocolTest, explicit?: string) {
    const raw = explicit ?? values[test.id] ?? '';
    if (raw === '') return;
    const r = resultBody(test, raw);
    if (!r.ok) { setRowErrors((m) => ({ ...m, [test.id]: r.message })); return; }
    setSavingRow(test.id); setError(null);
    try {
      await start();
      await api.post(path('results'), r.body, crypto.randomUUID());
      setValues((m) => { const n = { ...m }; delete n[test.id]; return n; });
      const nothingLeft = tests.every((t) => t.id === test.id || latest.has(t.id));
      if (nothingLeft && !tubesOwed) {
        await complete();
        onChanged(`${test.name} recorded. ${name} completed.`);
      } else {
        onChanged(`${test.name} recorded.`);
      }
      // Hands go to the next empty box, not the mouse.
      const inputs = Array.from(document.querySelectorAll<HTMLInputElement | HTMLSelectElement>('[id^="v-"]'));
      const me = inputs.findIndex((el) => el.id === `v-${test.id}`);
      inputs.slice(me + 1).find((el) => !el.disabled && el.value === '')?.focus();
    } catch (e) {
      setRowErrors((m) => ({ ...m, [test.id]: errorMessage(e) }));
    } finally { setSavingRow(null); }
  }

  return (
    <section className="rounded-card border border-neutral-200 bg-white" aria-labelledby={`st-${station.id}`}>
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-neutral-100 px-4 py-3">
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
          <h2 id={`st-${station.id}`} className="text-h4 text-neutral-900">{name}</h2>
          <StatusBadge tone={stationTone(station.status)}>
            {station.status === 'COMPLETED' && <Check className="h-3 w-3" aria-hidden />}
            {stationWord(station.status, closed)}
          </StatusBadge>
          {tests.length > 0 && (
            <span className="text-caption text-neutral-600">{done} of {tests.length} recorded</span>
          )}
        </div>

        {!readOnly && (
          <div className="flex flex-wrap items-center gap-2 max-md:w-full">
            {/* A station with readings starts on its first value; only one
                with nothing to record is started by hand. A draw-only station
                closes itself when the last tube owed is drawn. */}
            {station.status === 'PENDING' && tests.length === 0 && !drawsSamples && (
              <Button size="sm" variant="secondary" loading={busy}
                onClick={() => act(() => start(), `${name} started.`)}>
                Start station
              </Button>
            )}
            {station.status === 'IN_PROGRESS' && (
              <>
                <Input
                  value={notes}
                  onChange={(ev) => setNotes(ev.target.value)}
                  placeholder="Note (booth, noise, device…)"
                  className="h-8 w-52 text-small max-md:h-10 max-md:w-full"
                  aria-label={`Note for ${name}`}
                />
                <ClinicalComposerButton contexts={['STATION_NOTE']} value={notes} onAccept={(t) => setNotes(t.slice(0, 500))} label="Compose note" />
                <Button
                  size="sm"
                  variant={filled.length ? 'secondary' : 'primary'}
                  loading={busy && !filled.length}
                  disabled={busy || requiredLeft > 0 || tubesOwed}
                  title={requiredLeft > 0 ? 'Record every required test first'
                    : tubesOwed ? 'Draw the samples first' : undefined}
                  onClick={() => act(() => complete(), `${name} completed.`)}
                >
                  Complete station
                </Button>
              </>
            )}
          </div>
        )}
      </div>

      {editable && station.status === 'IN_PROGRESS' && requiredLeft > 0 && (
        <p className="border-b border-neutral-100 px-4 py-2 text-caption text-neutral-600">
          {requiredLeft} required test{requiredLeft === 1 ? '' : 's'} left before this station can be completed.
        </p>
      )}
      {editable && station.status === 'IN_PROGRESS' && tubesOwed && (
        <p className="border-b border-neutral-100 px-4 py-2 text-caption text-warning-600">
          No sample drawn yet. Drawing every tube owed under “Samples for the laboratory” completes this station.
        </p>
      )}

      <div className="divide-y divide-neutral-100">
        {tests.length === 0 && (
          <p className="px-4 py-3 text-small text-neutral-600">
            {drawsSamples
              ? 'Samples are drawn here. Their results are entered by the laboratory — see Samples for the laboratory below.'
              : 'No tests from this protocol are measured here.'}
          </p>
        )}
        {groupTests(station.station, tests).map(([heading, list]) => (
          <div key={heading ?? '_'}>
            {heading && (
              <h3 className="bg-neutral-50 px-4 py-1.5 text-caption font-medium uppercase tracking-wide text-neutral-600">{heading}</h3>
            )}
            {list.map((t) => (
              <TestRow
                key={t.id}
                test={t}
                current={latest.get(t.id) ?? null}
                disabled={!editable || busy}
                hidden={!editable}
                seesValue={seesValues}
                value={values[t.id] ?? ''}
                onChange={(v) => setValue(t.id, v)}
                saving={savingRow === t.id}
                error={rowErrors[t.id] ?? null}
                onRecord={() => void recordOne(t)}
                derived={derivable(t) ? (derived?.body.bmi ?? null) : undefined}
              />
            ))}
          </div>
        ))}
      </div>

      {derived?.any && seesValues && (
        <div className="border-t border-neutral-100">
          <DerivedVitalsPanel derived={derived} />
        </div>
      )}

      {station.station !== 'PHLEBOTOMY' && (
        <StationAttachments
          encounterId={encounterId}
          station={station.station}
          attachments={attachments}
          editable={!readOnly && !closed}
          onChanged={onChanged}
        />
      )}

      {editable && tests.length > 0 && (normalable.length > 0 || filled.length > 0) && (
        // The station's own foot: fill the normal answers, then record the
        // lot. Held at the foot of a phone's screen like the desk's button.
        <div className="flex flex-wrap items-center justify-between gap-2 border-t border-neutral-100 bg-neutral-50 px-4 py-2.5 max-md:sticky max-md:bottom-0 max-md:z-10">
          <span className="text-caption text-neutral-600">
            {filled.length
              ? `${filled.length} value${filled.length === 1 ? '' : 's'} ready to record${closesOnRecord ? ' — the station completes with them.' : '.'}`
              : 'Nothing typed yet.'}
          </span>
          <div className="flex flex-wrap items-center gap-2">
            {normalable.length > 0 && (
              <span className="inline-flex items-center gap-1">
                <Button size="sm" variant="secondary" disabled={busy}
                  onClick={() => setValues((m) => {
                    const n = { ...m };
                    for (const t of normalable) n[t.id] = normalAnswer(t)!;
                    return n;
                  })}>
                  Mark {normalable.length === 1 ? '1' : `all ${normalable.length}`} normal
                </Button>
                <InfoTip label="About marking normal">
                  Fills the normal answer into each yes/no or fixed-answer test that has exactly one. Readings with a number,
                  and tests with more than one normal answer, are left for you. Nothing is saved until you record.
                </InfoTip>
              </span>
            )}
            {filled.length > 0 && (
              <Button size="sm" loading={busy} onClick={() => void recordAll()}>
                {closesOnRecord ? `Record ${filled.length} and complete` : `Record ${filled.length === 1 ? '1' : `all ${filled.length}`}`}
              </Button>
            )}
          </div>
        </div>
      )}

      {error && (
        <div role="alert" className="border-t border-danger-600/20 bg-danger-100 px-4 py-2 text-small text-danger-600">
          {error}
        </div>
      )}
    </section>
  );
}

/**
 * The machine's own paper, beside the readings typed from it: an ECG strip,
 * a spirometry printout, an audiogram, a film. Attached as a PDF or an
 * image; the values stay the result.
 */
function StationAttachments({ encounterId, station, attachments, editable, onChanged }: {
  encounterId: string;
  station: string;
  attachments: EncounterAttachmentRow[];
  editable: boolean;
  onChanged: (message: string) => void;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function attach(file: File) {
    setBusy(true); setError(null);
    try {
      const content = await new Promise<string>((resolve, reject) => {
        const r = new FileReader();
        r.onload = () => resolve(String(r.result).split(',')[1] ?? '');
        r.onerror = () => reject(new Error('Could not read the file.'));
        r.readAsDataURL(file);
      });
      await api.post(`/encounters/${encounterId}/stations/${station}/attachments`, {
        fileName: file.name, mimeType: file.type || 'application/octet-stream', content,
      }, crypto.randomUUID());
      onChanged(`${file.name} attached to ${STATION_LABEL[station] ?? station}.`);
    } catch (e) { setError(errorMessage(e)); } finally { setBusy(false); }
  }
  async function open(id: string) {
    setError(null);
    try {
      const grant = await api.get<{ url: string }>(`/encounters/${encounterId}/attachments/${id}/download`);
      window.open(grant.url, '_blank', 'noopener');
    } catch (e) { setError(errorMessage(e)); }
  }
  async function remove(id: string) {
    setBusy(true); setError(null);
    try {
      await api.del(`/encounters/${encounterId}/attachments/${id}`);
      onChanged('Report file removed.');
    } catch (e) { setError(errorMessage(e)); } finally { setBusy(false); }
  }

  if (!editable && attachments.length === 0) return null;
  return (
    <div className="border-t border-neutral-100 px-4 py-2.5">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="inline-flex items-center gap-1 text-caption font-medium uppercase tracking-wide text-neutral-600">
          <Paperclip className="h-3.5 w-3.5" aria-hidden /> Report files
          <InfoTip label="About report files">
            The machine's own printout or trace — a PDF or a photo — kept beside the readings typed from it. The readings
            stay the result; the file is what they were read from.
          </InfoTip>
        </span>
        {editable && (
          <label className={cn('inline-flex h-8 cursor-pointer items-center rounded-btn border border-neutral-300 bg-white px-3 text-caption text-neutral-800 hover:bg-neutral-50', busy && 'opacity-60')}>
            {busy ? 'Attaching…' : 'Attach a report file'}
            <input
              type="file" className="sr-only" accept="application/pdf,image/jpeg,image/png,image/webp" disabled={busy}
              onChange={(ev) => { const f = ev.target.files?.[0]; ev.target.value = ''; if (f) void attach(f); }}
            />
          </label>
        )}
      </div>
      {attachments.length > 0 && (
        <ul className="mt-1 divide-y divide-neutral-100">
          {attachments.map((a) => (
            <li key={a.id} className="flex flex-wrap items-center justify-between gap-2 py-1.5">
              <span className="min-w-0 text-small text-neutral-800">
                {a.label}{a.label !== a.fileName ? <span className="text-neutral-500"> · {a.fileName}</span> : null}
                <span className="text-caption text-neutral-500"> · {Math.max(1, Math.round(a.sizeBytes / 1024))} KB</span>
              </span>
              <RowActions>
                <IconAction label={`Open ${a.label}`} icon={Eye} onClick={() => void open(a.id)} />
                {editable && <IconAction label={`Remove ${a.label}`} icon={Trash2} danger disabled={busy} onClick={() => void remove(a.id)} />}
              </RowActions>
            </li>
          ))}
        </ul>
      )}
      {error && <p role="alert" className="mt-1 text-caption text-danger-600">{error}</p>}
    </div>
  );
}

function TestRow({ test, current, disabled, hidden, seesValue, value, onChange, saving, error, onRecord, derived }: {
  test: ProtocolTest;
  current: EncounterResultRow | null;
  /**
   * Set (to a number or null) for a test the API records from other readings
   * — BMI from height and weight. The row shows the figure and takes nothing.
   */
  derived?: number | null;
  /** Inputs present but not usable right now (the station is saving). */
  disabled: boolean;
  /** No inputs at all: read-only, closed or completed. */
  hidden: boolean;
  seesValue: boolean;
  value: string;
  onChange: (v: string) => void;
  saving: boolean;
  error: string | null;
  onRecord: () => void;
}) {
  const kind = kindOf(test);

  // A fixed-answer test (colour vision, an ILO grade) offers its configured
  // answers, read from the Test Master when the row first renders. The
  // snapshot froze WHICH tests are owed, not how each is answered — and an
  // answer set configured after the order was raised must still reach the
  // station.
  //
  // They arrive with the encounter. The Test Master's own route is the
  // fallback for an older API only: it is company-scoped, so a venue login is
  // refused there — which is how every fixed-answer test came to read "no
  // answers configured" at a venue's station.
  const [options, setOptions] = useState<Array<{ code: string; display: string }> | null>(test.answerOptions ?? null);
  useEffect(() => {
    if (kind !== 'ENUM') return;
    if (test.answerOptions) { setOptions(test.answerOptions); return; }
    let live = true;
    api.get<{ answerOptions?: Array<{ code: string; display: string; isActive?: boolean }> }>(`/tests/${test.id}`)
      .then((t) => { if (live) setOptions((t.answerOptions ?? []).filter((o) => o.isActive !== false)); })
      .catch(() => { if (live) setOptions([]); });
    return () => { live = false; };
  }, [kind, test.id, test.answerOptions]);

  const recordLabel = current ? 'Re-record' : 'Record';
  // A free-text field with a list behind it offers the list as it is typed;
  // anything else typed is accepted and taught to the list on record.
  const suggestions = useSuggestions(kind === 'TEXT' ? suggestionListFor(test.code) : null);

  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-2 px-4 py-3">
      <div className="min-w-0 flex-[1_1_12rem]">
        <div className="flex items-center gap-2">
          <span className="text-small text-neutral-900">{test.name}</span>
          {test.isMandatory
            ? <span className="text-micro font-medium uppercase tracking-wide text-neutral-600">required</span>
            : <span className="text-micro uppercase tracking-wide text-neutral-500">optional</span>}
        </div>
        <div className="font-mono text-xs text-neutral-500">{test.code}</div>
      </div>

      {current && !seesValue && (
        <StatusBadge tone="covered"><Check className="h-3 w-3" aria-hidden />Recorded</StatusBadge>
      )}
      {!current && hidden && (
        <span className="text-caption text-neutral-500">Not recorded</span>
      )}

      {current && seesValue && test.code !== TOOTH_CHART_CODE && (
        <div className="flex items-center gap-2">
          <span className="text-sm tabular-nums text-neutral-800">
            {current.valueNumeric ?? current.valueText
              ?? (current.valueBoolean == null ? '—' : current.valueBoolean ? 'Normal' : 'Abnormal')}
            {current.unit ? ` ${current.unit}` : ''}
          </span>
          <StatusBadge tone={flagTone(current.flag)}>
            {(current.flag === 'CRITICAL_LOW' || current.flag === 'CRITICAL_HIGH') && <AlertTriangle className="h-3 w-3" aria-hidden />}
            {flagWord(current.flag, kind)}
          </StatusBadge>
          {current.attemptNo > 1 && (
            <span className="text-caption text-neutral-500">attempt {current.attemptNo}</span>
          )}
        </div>
      )}

      {derived !== undefined && !hidden && (
        <div className="flex flex-wrap items-center gap-2">
          {derived != null && current?.valueNumeric !== derived && (
            <span className="text-sm tabular-nums text-neutral-800">{derived}{test.unit ? ` ${test.unit}` : ''}</span>
          )}
          <span className="text-caption text-neutral-500">
            {derived == null
              ? 'Worked out once height and weight are in — nothing to type here.'
              : current?.valueNumeric === derived
                ? 'Recorded by the system from height and weight.'
                : 'Recorded by the system from height and weight when they are.'}
          </span>
        </div>
      )}

      {test.code === TOOTH_CHART_CODE && current && seesValue && (
        <p className="w-full text-caption text-neutral-700">{formatToothChart(current.valueText)}</p>
      )}
      {!hidden && derived === undefined && test.code === TOOTH_CHART_CODE ? (
        <div className="flex w-full flex-col gap-2">
          <ToothChart value={value} onChange={onChange} disabled={disabled || saving} />
          <div>
            <Button size="sm" variant="ghost" loading={saving} disabled={disabled || value === ''} onClick={onRecord}>
              {recordLabel}
            </Button>
          </div>
        </div>
      ) : !hidden && derived === undefined && (
        <div className="flex w-full flex-wrap items-center gap-2 sm:w-auto">
          {kind === 'ENUM' ? (
            options === null ? (
              <span className="text-caption text-neutral-500">Loading answers…</span>
            ) : options.length === 0 ? (
              <span className="text-caption text-overdue-600">No answers configured for this test — ask the platform to set them.</span>
            ) : options.length <= 4 ? (
              // A short answer set is tapped, not chosen from a list: the
              // questionnaire's yes / no / unknown / not assessed.
              <div className="flex flex-wrap items-center gap-1" role="group" aria-label={test.name}>
                {options.map((o) => (
                  <button
                    key={o.code}
                    type="button"
                    disabled={disabled || saving}
                    onClick={() => onChange(value === o.code ? '' : o.code)}
                    aria-pressed={value === o.code}
                    className={cn(
                      'h-9 rounded-btn border px-3 text-small max-md:h-10',
                      value === o.code
                        ? 'border-primary-600 bg-primary-50 font-semibold text-primary-700'
                        : 'border-neutral-300 bg-white text-neutral-800 hover:bg-neutral-50',
                    )}
                  >
                    {o.display}
                  </button>
                ))}
              </div>
            ) : (
              <>
                <label className="sr-only" htmlFor={`v-${test.id}`}>{test.name}</label>
                <Select
                  id={`v-${test.id}`}
                  value={value}
                  disabled={disabled || saving}
                  onChange={(ev) => onChange(ev.target.value)}
                  className="h-9 w-56 text-small max-md:h-10 max-md:w-full"
                >
                  <option value="">Choose…</option>
                  {options.map((o) => <option key={o.code} value={o.code}>{o.display}</option>)}
                </Select>
              </>
            )
          ) : kind === 'BOOLEAN' ? (
            <div className="flex items-center gap-1" role="group" aria-label={test.name}>
              {([['true', 'Normal'], ['false', 'Abnormal']] as const).map(([v, label]) => (
                <button
                  key={v}
                  type="button"
                  disabled={disabled || saving}
                  onClick={() => onChange(value === v ? '' : v)}
                  aria-pressed={value === v}
                  className={cn(
                    'h-9 rounded-btn border px-3 text-small max-md:h-10',
                    value === v
                      ? 'border-primary-600 bg-primary-50 font-semibold text-primary-700'
                      : 'border-neutral-300 bg-white text-neutral-800 hover:bg-neutral-50',
                  )}
                >
                  {label}
                </button>
              ))}
            </div>
          ) : (
            <>
              <label className="sr-only" htmlFor={`v-${test.id}`}>{test.name}</label>
              <Input
                id={`v-${test.id}`}
                value={value}
                disabled={disabled || saving}
                onChange={(ev) => onChange(ev.target.value)}
                onKeyDown={(ev) => { if (ev.key === 'Enter') onRecord(); }}
                aria-invalid={error ? true : undefined}
                inputMode={kind === 'NUMERIC' ? 'decimal' : 'text'}
                placeholder={current ? 'Re-measure' : (suggestions.length ? 'Type or pick…' : 'Value')}
                list={suggestions.length ? `dl-${test.id}` : undefined}
                className={cn('h-9 text-small max-md:h-10', kind === 'NUMERIC' ? 'w-36 text-right tabular-nums' : suggestions.length ? 'w-64 max-md:w-full' : 'w-36')}
              />
              {suggestions.length > 0 && (
                <datalist id={`dl-${test.id}`}>{suggestions.map((s) => <option key={s} value={s} />)}</datalist>
              )}
              {test.unit && <span className="text-xs text-neutral-500">{test.unit}</span>}
              {kind === 'TEXT' && composerContexts(test.code) && (
                <ClinicalComposerButton
                  contexts={composerContexts(test.code)!} value={value} onAccept={onChange}
                  disabled={disabled || saving} label="Compose"
                />
              )}
            </>
          )}
          {(kind !== 'ENUM' || (options?.length ?? 0) > 0) && (
            <Button size="sm" variant="ghost" loading={saving} disabled={disabled || value === ''} onClick={onRecord}>
              {recordLabel}
            </Button>
          )}
        </div>
      )}

      {error && (
        <p role="alert" className="w-full text-caption text-danger-600">{error}</p>
      )}
    </div>
  );
}

function stationTone(status: string): Tone {
  switch (status) {
    case 'COMPLETED': return 'covered';
    case 'IN_PROGRESS': return 'pending';
    case 'SKIPPED': return 'neutral';
    default: return 'neutral';
  }
}

/**
 * A result flag's tone. Deliberately not the fitness tones: a critical value
 * is not an "unfit" worker, and the clinical colours are kept for the four
 * decisions. Normal stays quiet so what is out of range is what the eye finds;
 * the badge always carries the flag's word, so colour is never the only signal.
 */
export function flagTone(flag: ResultFlag): Tone {
  switch (flag) {
    case 'CRITICAL_LOW': case 'CRITICAL_HIGH': return 'danger';
    case 'LOW': case 'HIGH': case 'ABNORMAL': return 'warning';
    default: return 'neutral';
  }
}

function age(dob: string | null): string {
  if (!dob) return 'age not recorded';
  const d = new Date(dob);
  const now = new Date();
  let years = now.getFullYear() - d.getFullYear();
  const m = now.getMonth() - d.getMonth();
  if (m < 0 || (m === 0 && now.getDate() < d.getDate())) years--;
  return `${years} years`;
}


function DeriveSamples({ encounterId, onDone }: { encounterId: string; onDone: (created: number, phlebotomyCompleted: boolean) => void }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  async function run() {
    setBusy(true); setError(null);
    try {
      const r = await api.post<{ created: Array<{ barcode: string; status?: string }>; phlebotomyCompleted?: boolean }>(
        `/lab/encounters/${encounterId}/samples/derive`, {}, crypto.randomUUID());
      onDone(r.created.length, !!r.phlebotomyCompleted);
    } catch (e) { setError(errorMessage(e)); } finally { setBusy(false); }
  }
  return (
    <>
      <Button size="sm" onClick={run} loading={busy} title="One barcode per tube the order's tests need">Draw every tube owed</Button>
      {error && <Alert className="mt-2">{error}</Alert>}
    </>
  );
}

/**
 * Drawing a sample. The barcode is minted by the server, so the desk only
 * says what kind of tube it is; the label is printed from the barcode shown
 * afterwards. One tube per call — a protocol needing EDTA and serum is two
 * draws, and two chains of custody.
 */
const SAMPLE_TYPES: Array<[string, string, string]> = [
  ['BLOOD_EDTA', 'Blood — EDTA (lavender)', 'CBC, HbA1c'],
  ['BLOOD_SERUM', 'Blood — serum (red / gold)', 'Biochemistry, lipids, LFT, KFT'],
  ['BLOOD_FLUORIDE', 'Blood — fluoride (grey)', 'Glucose'],
  ['URINE', 'Urine', 'Routine, microscopy'],
  ['SPUTUM', 'Sputum', 'AFB'],
];

function DrawSampleDialog({ encounterId, tests, onClose, onDone }: {
  encounterId: string; tests: ProtocolTest[]; onClose: () => void; onDone: (barcode: string) => void;
}) {
  const [sampleType, setSampleType] = useState('BLOOD_EDTA');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function draw() {
    setBusy(true); setError(null);
    try {
      const s = await api.post<{ barcode: string }>(
        `/lab/encounters/${encounterId}/samples`, { sampleType }, crypto.randomUUID());
      onDone(s.barcode);
    } catch (e) { setError(errorMessage(e)); setBusy(false); }
  }

  return (
    <Dialog title="Draw a sample" info="The barcode is issued when you confirm. Write it on the tube, or print the label from it." onClose={onClose}>
      <p className="mt-1 text-xs text-neutral-500">
        Owed by this protocol: {tests.map((t) => t.name).join(', ') || 'no pathology tests'}.
      </p>
      <SelectField label="Tube" value={sampleType} onChange={setSampleType} className="mt-3"
        hint={SAMPLE_TYPES.find((t) => t[0] === sampleType)?.[2]}>
        {SAMPLE_TYPES.map(([v, label]) => <option key={v} value={v}>{label}</option>)}
      </SelectField>
      {error && <Alert className="mt-3">{error}</Alert>}
      <div className="mt-5 flex justify-end gap-2">
        <Button variant="secondary" onClick={onClose} disabled={busy}>Cancel</Button>
        <Button onClick={draw} loading={busy}>Draw and issue barcode</Button>
      </div>
    </Dialog>
  );
}


/** One click for every open station that already has all its tests. */
function CompleteAll({ encounterId, stations, onDone }: {
  encounterId: string; stations: string[]; onDone: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  async function go() {
    setBusy(true); setError(null);
    try {
      for (const st of stations) {
        await api.post(`/encounters/${encounterId}/stations/${st}/complete`, {}, crypto.randomUUID());
      }
      onDone();
    } catch (e) { setError(errorMessage(e)); } finally { setBusy(false); }
  }
  return (
    <div className="flex flex-wrap items-center justify-between gap-2 rounded-card border border-primary-200 bg-primary-50 px-4 py-2">
      <span className="text-small text-neutral-700">
        {stations.length} stations have every test recorded and are still open: {stations.map((s) => STATION_LABEL[s as never] ?? s).join(', ')}.
      </span>
      <Button size="sm" loading={busy} onClick={go}>Complete all {stations.length}</Button>
      {error && <p role="alert" className="w-full text-caption text-danger-600">{error}</p>}
    </div>
  );
}
